import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useImmer } from "use-immer";
import AuthContext from "../../contexts/AuthProvider";
import { useAIChatFilters } from "../../contexts/AIChatFiltersContext";

import "moment/locale/es";
import "moment/locale/pt";
import cx from "classnames";
import { client, fileDownloadClient } from "../../services/axiosClient";
import { useTranslation } from "react-i18next";
import { objectsEqual, calculateOomFromPlValues } from "../../utils/utils";
import { useScreenshot, createFileName } from "use-react-screenshot";
import fileDownload from "js-file-download";
import { toast, Slide as ToastSlide } from "react-toastify";
import useThemeColors from "../../hooks/useThemeColors";

import DraggableCanvas from "../../components/Draggable/DraggableCanvas";
import Treemap from "../../components/Graphs/Treemap";
import Pnl from "../../components/Pnl/Pnl";
import HelpDialog from "../../components/Dialog/HelpDialog";
import { PnlSharedProvider } from "../../hooks/usePnlShared";
import { ThreeDots } from "react-loader-spinner";
import FilterTag from "../../components/FilterTag/FilterTag";
import FilterDropdown from "../../components/FilterDropdown/FilterDropdown";
import ResultsFilterBar from "../../components/ResultsFilterBar";
import DimFilterDialog from "../../components/ResultsFilterBar/DimFilterDialog";
import { useResultsFilters } from "../../hooks/useResultsFilters";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Hidden from "@material-ui/core/Hidden";
import Popper from "@material-ui/core/Popper";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";

// @material-ui/icons
import {
  AttachMoney,
  HelpOutline,
  PhotoCamera,
  GetApp,
  Fullscreen,
  FullscreenExit,
  Traffic,
} from "@material-ui/icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCrosshairs,
} from "@fortawesome/free-solid-svg-icons";
// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import ActionBar from "components/ActionBar/ActionBar";
import ActionDropdown from "../../components/ActionDropdown/ActionDropdown";

import styles from "../../assets/jss/material-dashboard-pro-react/views/graphExplorerStyle";
import FullPageLoader from "../../components/Loaders/FullPageLoader";

const NUM_GROUPS = 5;

export default function GraphExplorer() {
  const { userDetails } = useContext(AuthContext);
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor } = useThemeColors();

  const [isLoading, setIsLoading] = useState(true);
  const [isTreemapLoading, setIsTreemapLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  // structure & selection states
  const [dimensions, setDimensions] = useState(["placeholder"]);
  const [dimensionNames, setDimensionNames] = useState({
    placeholder: { en: "placeholder", es: "placeholder", pt: "placeholder" },
  });
  const [dimensionGroups, setDimensionGroups] = useState([]);
  const [groupsName, setGroupsName] = useState("");

  const [levels, setLevels] = useImmer(
    Array.from({ length: NUM_GROUPS }, (_, i) => i).reduce((acc, key) => {
      acc[key] = null;
      return acc;
    }, {}),
  );
  const [selection, setSelection] = useImmer({});
  const [pnlSelection, setPnlSelection] = useImmer({});
  const levelsCount = useRef(0);
  const childrenLevel = useRef(null);
  const [parentLevel, setParentLevel] = useState(-1);

  const [selectedBox, setSelectedBox] = useImmer({
    display: false,
    items: [], // Array of {x, y, width, height, parentName, childrenName}
  });
  const [treemapData, setTreemapData] = useState([]);
  const totalNodes = useRef(0);
  const totalSize = useRef(0);

  // P&L
  const volumeUnit = useRef("");
  const [volumeColumn, setVolumeColumn] = useState("volume");
  const volumeUnitAlternatives = useRef([]);
  const plStructure = useRef([]);
  const netRevenueAnchor = useRef("");
  const [revenueReference, setRevenueReference] = useState(null);
  const revenueReferenceOptions = useRef([]);
  const serveMarginAnchor = useRef("");
  const revenueAnchors = useRef([]);
  const volumeL12M = useRef(null);
  const [plRefreshing, setPlRefreshing] = useState(false);
  const [plValues, setPlValues] = useState({ 1: { volume: 0 } });
  const [plName, setPlName] = useState(userDetails.implementation_name);
  const [followMeText, setFollowMeText] = useState([
    userDetails.implementation_name,
  ]);
  const [lastFollowMeText, setLastFollowMeText] = useState([
    userDetails.implementation_name,
  ]);
  const followMeTextTimer = useRef(null);
  const lastRefreshPnlParams = useRef("");
  const lastRefreshTreemapParams = useRef({});
  const [oom, setOom] = useState({
    div: 1e6,
    x: " " + t("oom.m"),
  });

  // benchmark
  const [benchmark, setBenchmark] = useState("total_company");

  // other states
  const [currencyUnit, setCurrencyUnit] = useState("");
  const currencyUnitOptions = useRef([]);
  const currencyUnitOptionsAvailable = useRef([]);
  const [colorMetric, setColorMetric] = useState("serve_margin");
  const [drillDownsOpen, setDrillDownsOpen] = useState([]);
  const [selectedColorLine, setSelectedColorLine] = useState("");
  const eligibleColorLines = useRef([]);
  const [isCostLine, setIsCostLine] = useState(false);
  const controllerRef = useRef();
  const treemapControllerRef = useRef();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Scale mode states (global vs local)
  const [scaleMode, setScaleMode] = useState("global");
  const [globalStats, setGlobalStats] = useState({
    serveMargin: { p5: 0, p25: 0, p50: 0, p75: 0, p95: 0, weightedMean: 0 },
    valuePerVolume: { p5: 0, p25: 0, p50: 0, p75: 0, p95: 0, weightedMean: 0 },
  });
  const [localStats, setLocalStats] = useState({
    serveMargin: { p5: 0, p25: 0, p50: 0, p75: 0, p95: 0, weightedMean: 0 },
    valuePerVolume: { p5: 0, p25: 0, p50: 0, p75: 0, p95: 0, weightedMean: 0 },
  });

  // Reference range filters are now managed by useResultsFilters internally.
  // getDimInfoExtra provides fixedRefLine metadata to the hook for Graph Explorer.

  const getDimInfoExtra = useCallback(
    (filterKey) => {
      if (filterKey === "period") return {};
      const line = eligibleColorLines.current.find(
        (l) => l.meta_name === selectedColorLine,
      );
      const lineLabel = line ? line.name : "";
      return {
        hasRefRange: true,
        fixedRefLine: selectedColorLine
          ? { key: selectedColorLine, label: lineLabel }
          : null,
      };
    },
    [selectedColorLine],
  );

  const {
    filtersList,
    filtersNames,
    filtersGroups,
    commonFilters,
    commonFiltersIndexes,
    commonFiltersMode,
    commonFiltersSequence,
    filterMenuClickHandler,
    removeFilter,
    dimFilterDialogProps,
    filtersListTranslated,
    filtersActiveList,
    filtersSequenceNums,
    getFiltersInSequence,
    getFilterItems,
    persist,
    setPersist,
    dimSections,
    handleResetFilters,
    availablePeriods,
    selectedPeriodKeys,
    activePreset,
    handlePeriodApply,
    rangeFilters,
    clearRangeFilter,
  } = useResultsFilters({
    metaEndpoint: "api/results_graph_explorer_meta/",
    onMetaLoaded: (response) => {
      setDimensions(response.data.dimensions);
      setDimensionNames(response.data.dimension_names);
      setDimensionGroups(response.data.dimension_groups || []);
      volumeUnit.current = response.data.volume_unit;
      volumeUnitAlternatives.current =
        response.data.volume_unit_alternatives || [];
      currencyUnitOptions.current = response.data.currency_unit_options;
      currencyUnitOptionsAvailable.current =
        response.data.currency_unit_options_available;
      setCurrencyUnit(response.data.default_currency_unit);
      plStructure.current = response.data.pl_structure;
      netRevenueAnchor.current = response.data.net_revenue_anchor;
      revenueReferenceOptions.current =
        response.data.revenue_reference_options || [];
      if (
        revenueReference === null &&
        revenueReferenceOptions.current.length > 0
      ) {
        const defaultOpt = revenueReferenceOptions.current.find(
          (o) => o.default,
        );
        setRevenueReference(
          defaultOpt ? defaultOpt.value : response.data.net_revenue_anchor,
        );
      }
      serveMarginAnchor.current = response.data.serve_margin_anchor;
      revenueAnchors.current = response.data.revenue_anchors;
      if (response.data.eligible_color_lines) {
        eligibleColorLines.current = response.data.eligible_color_lines;
      }
      setSelectedColorLine(response.data.serve_margin_anchor);
      setIsLoading(false);
    },
    getDimInfoExtra,
  });

  // Screenshot functionality
  const graphExplorerRef = useRef(null);
  const [, takeScreenShot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });
  const download = (
    image,
    { name = "graph_explorer_screenshot", extension = "png" } = {},
  ) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };
  const downloadScreenshot = () =>
    takeScreenShot(graphExplorerRef.current).then(download);

  const notify = (text, type = "info", time = 3000) =>
    toast(text, {
      position: "top-right",
      autoClose: time,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });

  const handleDownloadExcel = () => {
    if (isDownloading || isLoading) return;

    setIsDownloading(true);
    notify(t("results.download_generating"), "info");

    const params = {
      levels: levels,
      selection: selection,
      dimensions: dimensions,
      dimension_names: dimensionNames,
      currency_unit: currencyUnit,
      volume_column: volumeColumn,
      revenue_reference: revenueReference,
      parent_level: parentLevel,
      common_filters: commonFilters,
      common_filters_mode: commonFiltersMode,
      common_filters_sequence: commonFiltersSequence,
      ref_range_filters:
        Object.keys(rangeFilters).length > 0 ? buildRangeFiltersArray() : null,
      color_line: selectedColorLine || serveMarginAnchor.current,
      filters_names: filtersNames.current,
      language: i18n.language,
    };

    client
      .post("api/results_graph_explorer_download_excel/", params)
      .then(() => {
        fileDownloadClient
          .post("api/results_graph_explorer_download_excel/", params)
          .then((response) => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, "0");
            const filename = `${year}_${month}_GraphExplorer_Extract.xlsx`;

            fileDownload(response.data, filename);
            setIsDownloading(false);
          })
          .catch((error) => {
            console.log(error);
            notify(t("results.download_error"), "error");
            setIsDownloading(false);
          });
      })
      .catch((error) => {
        console.log(error);
        notify(t("results.download_error"), "error");
        setIsDownloading(false);
      });
  };

  const getDownloadOptions = () => {
    let options = [];
    options.push({
      identified: { option_type: "download_excel" },
      text: t("results.download_excel"),
      disabled: isDownloading,
    });
    return options;
  };

  const handleDownloadSelection = (opt) => {
    const { option_type } = opt;
    if (option_type === "download_excel") {
      handleDownloadExcel();
    }
  };

  const getUnitOptions = () => {
    let options = [];
    // currencies
    options.push({
      explainer: true,
      text: t("results.unit_blocks.currency"),
    });
    currencyUnitOptions.current.map((opt) => {
      let available = currencyUnitOptionsAvailable.current.includes(opt);
      options.push({
        identified: {
          option_type: "currency",
          value: opt,
        },
        text:
          opt +
          (available
            ? ""
            : " (" +
              t("results.missing_rates") +
              (["DEV", "IMG", "CAD", "CFA", "CST"].includes(
                userDetails.user_role,
              )
                ? " - " + t("results.missing_rates_complement")
                : "") +
              ")"),
        active: opt === currencyUnit,
        disabled: !available,
      });
    });
    // revenue reference (only show if more than 1 non-combined option)
    const filteredRevRefOptions = revenueReferenceOptions.current.filter(
      (opt) => opt.value !== "combined",
    );
    if (filteredRevRefOptions.length > 1) {
      options.push({ divider: true });
      options.push({
        explainer: true,
        text: t("results.unit_blocks.revenue_reference"),
      });
      filteredRevRefOptions.forEach((opt) => {
        options.push({
          identified: {
            option_type: "revenue_reference",
            value: opt.value,
          },
          text: opt.label,
          active: revenueReference === opt.value,
        });
      });
    }
    // volume unit alternatives (only show if more than 1 option)
    if (volumeUnitAlternatives.current.length > 1) {
      options.push({ divider: true });
      options.push({
        explainer: true,
        text: t("results.unit_blocks.volume"),
      });
      volumeUnitAlternatives.current.forEach((opt) => {
        options.push({
          identified: {
            option_type: "volume",
            value: opt.column,
          },
          text: opt.label,
          active: volumeColumn === opt.column,
        });
      });
    }
    options.push({ divider: true });
    // display metric
    options.push({
      explainer: true,
      text: t("results.unit_blocks.color_metric"),
    });
    options.push({
      identified: {
        option_type: "color_metric",
        value: "serve_margin",
      },
      text: t("results.nominal_value_short"),
      active: colorMetric === "serve_margin",
    });
    options.push({
      identified: {
        option_type: "color_metric",
        value: "per_volume",
      },
      text: t("results.value") + " / " + volumeUnit.current,
      active: colorMetric === "per_volume",
    });
    return options;
  };
  const handleUnitSelection = (opt) => {
    const { option_type, value } = opt;
    if (option_type === "currency") {
      if (value !== currencyUnit) {
        setCurrencyUnit(value);
      }
    } else if (option_type === "color_metric") {
      setColorMetric(value);
    } else if (option_type === "revenue_reference") {
      setRevenueReference(value);
    } else if (option_type === "volume") {
      if (value !== volumeColumn) {
        setVolumeColumn(value);
        const selectedAlt = volumeUnitAlternatives.current.find(
          (o) => o.column === value,
        );
        if (selectedAlt) {
          volumeUnit.current = selectedAlt.label;
        }
      }
    }
  };

  // handleColorLineSelection is now inline in the P&L tree selector

  const handleHelpClick = () => {
    setHelpDialogOpen(true);
  };

  // Build filter scope data for the Pnl component
  const buildFilterScopeData = () => {
    const scopeData = {
      viewType: "graph_explorer",
    };

    // Process each P&L (GraphExplorer only has one P&L, so use "1")
    const numPL = "1";
    const activeFilters = [];

    // Add P&L-specific filters from GraphExplorer
    Object.values(levels)
      .filter((val) => val !== null)
      .forEach((val) => {
        if (Object.keys(pnlSelection).includes(val)) {
          const selectedValue = pnlSelection[val];
          const selectionArray = Array.isArray(selectedValue)
            ? selectedValue
            : [selectedValue];
          // Skip if the value starts and ends with ● (these are special markers)
          const filteredItems = selectionArray.filter(
            (item) => !(item.startsWith("●") && item.endsWith("●")),
          );
          if (filteredItems.length > 0) {
            // Get the translated dimension name
            const translatedName = dimensionNames[val]?.[i18n.language] || val;

            activeFilters.push({
              name: translatedName,
              type: "pl_specific",
              filterName: val,
              items: filteredItems,
            });
          }
        }
      });

    scopeData[numPL] = activeFilters;
    return scopeData;
  };

  const refreshPL = () => {
    if (isLoading) return;
    let pl_filters = {};
    let pl_filters_mode = {};
    let pl_filters_sequence = [];
    let pl_name = [userDetails.implementation_name];
    Object.values(levels)
      .filter((val) => val !== null)
      .forEach((val) => {
        if (Object.keys(pnlSelection).includes(val)) {
          const selectionValue = pnlSelection[val];
          const selectionArray = Array.isArray(selectionValue)
            ? selectionValue
            : [selectionValue];
          pl_filters[val] = selectionArray;
          pl_filters_mode[val] = "items";
          pl_filters_sequence.push(val);
          if (selectionArray.length > 1) {
            pl_name.push(`${selectionArray.length} ${t("results.selected")}`);
          } else {
            pl_name.push(selectionArray[0]);
          }
        }
      });
    function replaceValuesWithNull(dict) {
      let resp = {};
      for (let key in dict)
        resp[key] =
          dict[key][0].startsWith("●") && dict[key][0].endsWith("●")
            ? [""]
            : dict[key];
      return resp;
    }
    const params = {
      currency_unit: currencyUnit,
      volume_column: volumeColumn,
      revenue_reference: revenueReference,
      common_filters: commonFilters,
      common_filters_mode: commonFiltersMode,
      common_filters_sequence: commonFiltersSequence,
      pl_filters: replaceValuesWithNull(pl_filters),
      pl_filters_mode,
      pl_filters_sequence,
      ref_range_filters:
        Object.keys(rangeFilters).length > 0 ? buildRangeFiltersArray() : null,
      ref_color_line:
        Object.keys(rangeFilters).length > 0
          ? selectedColorLine || serveMarginAnchor.current
          : null,
    };
    if (JSON.stringify(params) === lastRefreshPnlParams.current) return;
    lastRefreshPnlParams.current = JSON.stringify(params);
    setPlRefreshing(true);
    setPlName(pl_name.join(" > "));
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    client
      .post("api/results_pl_explorer_read_pl/", params, {
        signal: controllerRef.current.signal,
      })
      .then((response) => {
        setPlValues({ 1: response.data.pl });
        setPlRefreshing(false);
        controllerRef.current = null;
      })
      .catch((error) => {
        if (error.code !== "ERR_CANCELED") {
          console.log(error);
          setPlRefreshing(false);
        }
        controllerRef.current = null;
      });
  };
  const refreshTreemap = () => {
    if (isLoading) return;
    function replaceValuesWithNull(dict) {
      let resp = {};
      for (let key in dict)
        resp[key] =
          dict[key].startsWith("●") && dict[key].endsWith("●") ? "" : dict[key];
      return resp;
    }
    const params = {
      level1: levels[parentLevel],
      level2: null,
      selection_filters: replaceValuesWithNull(selection),
      currency_unit: currencyUnit,
      volume_column: volumeColumn,
      revenue_reference: revenueReference,
      color_line: selectedColorLine || serveMarginAnchor.current,
      common_filters: commonFilters,
      common_filters_mode: commonFiltersMode,
      common_filters_sequence: commonFiltersSequence,
      ref_range_filters:
        Object.keys(rangeFilters).length > 0 ? buildRangeFiltersArray() : null,
    };
    if (
      JSON.stringify(params) ===
      JSON.stringify(lastRefreshTreemapParams.current)
    )
      return;
    lastRefreshTreemapParams.current = structuredClone(params);
    setIsTreemapLoading(true);
    if (treemapControllerRef.current) treemapControllerRef.current.abort();
    treemapControllerRef.current = new AbortController();
    client
      .post("api/results_graph_explorer_get_treemap_values/", params, {
        signal: treemapControllerRef.current.signal,
      })
      .then((response) => {
        setTreemapData(response.data.treemap_data);
        totalNodes.current = response.data.total_nodes;
        totalSize.current = response.data.total_size || 0;
        setIsCostLine(response.data.is_cost_line || false);
        // Store stats from treemap response (both global and local)
        const parseStats = (stats) => ({
          serveMargin: {
            p5: stats.serve_margin.p5,
            p25: stats.serve_margin.p25,
            p50: stats.serve_margin.p50,
            p75: stats.serve_margin.p75,
            p95: stats.serve_margin.p95,
            weightedMean: stats.serve_margin.weighted_mean,
          },
          valuePerVolume: {
            p5: stats.value_per_volume.p5,
            p25: stats.value_per_volume.p25,
            p50: stats.value_per_volume.p50,
            p75: stats.value_per_volume.p75,
            p95: stats.value_per_volume.p95,
            weightedMean: stats.value_per_volume.weighted_mean,
          },
        });
        if (response.data.global_stats) {
          setGlobalStats(parseStats(response.data.global_stats));
        }
        if (response.data.local_stats) {
          setLocalStats(parseStats(response.data.local_stats));
        }
        setIsTreemapLoading(false);
        treemapControllerRef.current = null;
        setSelectedBox((draft) => {
          draft.display = false;
          draft.items = [];
        });
      })
      .catch((error) => {
        if (error.code !== "ERR_CANCELED") {
          console.log(error);
          setIsTreemapLoading(false);
        }
        treemapControllerRef.current = null;
      });
  };

  useEffect(() => {
    i18n.changeLanguage(i18n.language.slice(0, 2));
  }, []);

  useEffect(() => {
    setGroupsName(t("results.graph_explorer.level"));
  }, [i18n.language]);

  useEffect(() => {
    if (isLoading) return;

    levelsCount.current = Object.values(levels).filter(
      (val) => val !== null,
    ).length;

    let selection_changed = false;
    let new_selection = structuredClone(selection);
    Object.keys(selection).forEach((key) => {
      if (!Object.values(levels).slice(0, parentLevel).includes(key)) {
        selection_changed = true;
        delete new_selection[key];
      }
    });
    if (selection_changed) {
      setSelection(new_selection);
      setPnlSelection(new_selection);
      return;
    }

    if (levelsCount.current > 0 && parentLevel === -1) {
      setParentLevel(0);
      return;
    }

    if (levelsCount.current === parentLevel) {
      setParentLevel(levelsCount.current - 1);
      return;
    }

    if (levelsCount.current === 0) {
      setTreemapData([]);
      lastRefreshTreemapParams.current = {};
      setSelectedBox((draft) => {
        draft.display = false;
        draft.items = [];
      });
    } else {
      if (
        Object.values(levels)
          .slice(0, parentLevel + 1)
          .every((val) => Object.keys(selection).includes(val))
      ) {
        let temp_selection = structuredClone(selection);
        delete temp_selection[levels[parentLevel]];
        if (
          !objectsEqual(
            temp_selection,
            lastRefreshTreemapParams.current.filters,
          )
        ) {
          refreshTreemap();
        }
      } else {
        refreshTreemap();
      }
    }

    refreshPL();
  }, [
    levels,
    selection,
    pnlSelection,
    parentLevel,
    currencyUnit,
    volumeColumn,
    commonFilters,
    commonFiltersMode,
    commonFiltersSequence,
    selectedColorLine,
    rangeFilters,
  ]);

  useEffect(() => {
    let new_drill_downs_open = [];
    plStructure.current.forEach((plLine) => {
      if (plLine.is_parent) new_drill_downs_open.push(plLine.meta_name);
    });
    setDrillDownsOpen(new_drill_downs_open);
  }, [plStructure.current]);

  useLayoutEffect(() => {
    // changes the order of magnitude in which all the P&Ls are presented
    //    - fired when the plValues or language is modified
    //    - store results in 'oom' object

    const calculatedOom = calculateOomFromPlValues(plValues, t);
    setOom(calculatedOom);
  }, [plValues, i18n.language, t]);

  const refreshFollowMeText = () => {
    let text_arr = [userDetails.implementation_name];
    Object.values(levels).forEach((val) => {
      if (!val || !selection[val]) return;
      text_arr.push(`${dimensionNames[val][i18n.language]}: ${selection[val]}`);
    });
    return text_arr;
  };
  useLayoutEffect(() => {
    const newFollowMeText = refreshFollowMeText();
    if (newFollowMeText === followMeText) return;
    setFollowMeText(newFollowMeText);
    clearTimeout(followMeTextTimer.current);
    if (newFollowMeText.length > lastFollowMeText.length) {
      followMeTextTimer.current = setTimeout(() => {
        setLastFollowMeText(newFollowMeText);
      }, 0);
    } else {
      setLastFollowMeText(newFollowMeText);
    }
  }, [levels, selection, i18n.language]);

  // Focus mode: ESC to exit
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && focusMode) {
        setFocusMode(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusMode]);

  const {
    setPageFilters: _setChatFilters,
    clearPageFilters: _clearChatFilters,
  } = useAIChatFilters();
  useEffect(() => {
    const summary = [];
    commonFiltersSequence.forEach((filterName) => {
      const items = commonFilters[filterName] || [];
      if (items.length === 0) return;
      const label =
        filtersNames.current[filterName]?.[i18n.language] || filterName;
      const values = getFilterItems(
        items,
        filterName,
        commonFiltersMode[filterName],
      );
      if (values && values.length > 0) {
        summary.push({ dimension: label, values });
      }
    });
    Object.entries(pnlSelection).forEach(([dim, val]) => {
      if (!dim || val == null) return;
      const label = dimensionNames[dim]?.[i18n.language] || dim;
      const values = Array.isArray(val) ? val : [val];
      if (values.length > 0) {
        summary.push({ dimension: label, values });
      }
    });
    _setChatFilters("Graph Explorer", summary);
    return () => _clearChatFilters();
  }, [
    commonFilters,
    commonFiltersMode,
    commonFiltersSequence,
    pnlSelection,
    dimensionNames,
    i18n.language,
  ]);

  // filtersListTranslated, filtersActiveList, filtersSequenceNums,
  // getFiltersInSequence, getFilterItems → fornecidos por useResultsFilters

  // Helper: converte rangeFilters (hook) para array esperado pelo backend
  const buildRangeFiltersArray = useCallback(
    () =>
      Object.entries(rangeFilters).map(([dim, f]) => ({
        dimension: dim,
        mode: f.mode,
        min: f.min,
        max: f.max,
        ref_line: f.refLine,
      })),
    [rangeFilters],
  );

  const dimNameForTag = (dim) => {
    const fn = filtersNames.current[dim];
    if (fn && fn[i18n.language]) return fn[i18n.language];
    const dn = dimensionNames[dim];
    if (dn && dn[i18n.language]) return dn[i18n.language];
    return dim;
  };

  // Get current labels for the discrete buttons
  const getCurrentUnitLabel = () => {
    const metric =
      colorMetric === "per_volume"
        ? t("results.value") + " / " + volumeUnit.current
        : t("results.nominal_value_short");
    return currencyUnit + " · " + metric;
  };

  const getCurrentRefLabel = () => {
    const line = eligibleColorLines.current.find(
      (l) => l.meta_name === selectedColorLine,
    );
    return line ? line.name : "";
  };

  const benchmarkOptions = useMemo(() => {
    const options = [];
    options.push({
      identified: { value: "total_company" },
      text: t("results.total_company"),
      active: benchmark === "total_company",
    });
    options.push({
      identified: { value: "external" },
      text: t("results.external"),
      active: benchmark === "external",
      disabled: true,
    });
    return options;
  }, [t, benchmark]);

  const handleBenchmarkSelection = (opt) => {
    const { value } = opt;
    setBenchmark(value);
  };

  const graphOptions = (
    <>
      {/* Download */}
      <ActionDropdown
        button={
          <GridItem className={classes.actionWrapperOuter}>
            <div
              className={cx({
                [classes.actionWrapper]: true,
                [classes.actionDisabled]:
                  isLoading ||
                  isTreemapLoading ||
                  levelsCount.current === 0 ||
                  isDownloading,
              })}
            >
              <div className={classes.actionIcon}>
                {isDownloading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <ThreeDots
                      height="20"
                      width="20"
                      color="#999999"
                      ariaLabel="loading"
                    />
                  </div>
                ) : (
                  <GetApp />
                )}
              </div>
              <h6 className={classes.actionText}>{t("results.download")}</h6>
            </div>
          </GridItem>
        }
        dropdownList={getDownloadOptions()}
        onClick={handleDownloadSelection}
      />
      {/* Take photo */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={cx({
            [classes.actionWrapper]: true,
            [classes.actionDisabled]:
              isLoading || isTreemapLoading || levelsCount.current === 0,
          })}
          onClick={levelsCount.current === 0 ? undefined : downloadScreenshot}
          style={{
            cursor:
              isLoading || isTreemapLoading || levelsCount.current === 0
                ? "default"
                : "pointer",
          }}
        >
          <div className={classes.actionIcon}>
            <PhotoCamera />
          </div>
          <h6 className={classes.actionText}>{t("results.take_photo")}</h6>
        </div>
      </GridItem>
      {/* Benchmarks */}
      <ActionDropdown
        button={
          <GridItem className={classes.actionWrapperOuter}>
            <div
              className={cx({
                [classes.actionWrapper]: true,
                [classes.actionDisabled]:
                  isLoading || levelsCount.current === 0,
              })}
            >
              <div className={classes.actionIcon}>
                <Traffic />
              </div>
              <h6 className={classes.actionText}>{t("results.benchmark")}</h6>
            </div>
          </GridItem>
        }
        dropdownList={benchmarkOptions}
        onClick={handleBenchmarkSelection}
      />
      {/* Focus */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={cx({
            [classes.actionWrapper]: true,
            [classes.actionDisabled]: isLoading || levelsCount.current === 0,
          })}
          onClick={
            isLoading || levelsCount.current === 0
              ? undefined
              : () => setFocusMode((f) => !f)
          }
          style={{
            cursor:
              isLoading || levelsCount.current === 0 ? "default" : "pointer",
          }}
        >
          <div className={classes.actionIcon}>
            {focusMode ? <FullscreenExit /> : <Fullscreen />}
          </div>
          <h6 className={classes.actionText}>
            {t("logistics_flow.focus_mode")}
          </h6>
        </div>
      </GridItem>
      {/* Help */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={cx({
            [classes.actionWrapper]: true,
            [classes.actionDisabled]: isLoading,
          })}
          onClick={handleHelpClick}
          style={{ cursor: isLoading ? "default" : "pointer" }}
        >
          <div className={classes.actionIcon}>
            <HelpOutline />
          </div>
          <h6 className={classes.actionText}>{t("results.help")}</h6>
        </div>
      </GridItem>
    </>
  );

  const followMe = (
    <>
      {followMeText.map((value, key) => (
        <div key={key} className={classes.followMe}>
          <span
            className={cx({
              [classes.followMeText]: true,
              ["new"]: !lastFollowMeText.includes(value),
            })}
          >
            {value}
          </span>
          {key + 1 < followMeText.length && (
            <span className={classes.followMeDivider}>
              <FontAwesomeIcon icon={faChevronRight} />
            </span>
          )}
        </div>
      ))}
    </>
  );

  const [refAnchorEl, setRefAnchorEl] = useState(null);

  const discreteControls = (
    <>
      {/* Unit button */}
      <ActionDropdown
        button={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              padding: "3px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
              background: "#fff",
              fontSize: 11,
              fontWeight: 500,
              color: "#444",
            }}
          >
            <AttachMoney style={{ fontSize: 14 }} />
            <span>{getCurrentUnitLabel()}</span>
          </div>
        }
        dropdownList={getUnitOptions()}
        onClick={handleUnitSelection}
        dropPlacement="bottom-start"
      />
      {/* Reference button + P&L tree selector */}
      <span>
        <div
          onClick={(e) => setRefAnchorEl(refAnchorEl ? null : e.currentTarget)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            cursor: "pointer",
            padding: "3px 10px",
            borderRadius: 6,
            border: "1px solid #ddd",
            background: "#fff",
            fontSize: 11,
            fontWeight: 500,
            color: "#444",
          }}
        >
          <FontAwesomeIcon icon={faCrosshairs} style={{ fontSize: 12 }} />
          <span>{getCurrentRefLabel()}</span>
        </div>
        <Popper
          open={Boolean(refAnchorEl)}
          anchorEl={refAnchorEl}
          transition
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper
                style={{
                  borderRadius: 6,
                  boxShadow: "0 4px 20px rgba(0,0,0,.12)",
                  maxHeight: 380,
                  overflowY: "auto",
                  minWidth: 220,
                  padding: "6px 0",
                }}
              >
                <ClickAwayListener onClickAway={() => setRefAnchorEl(null)}>
                  <div>
                    {eligibleColorLines.current.map((line) => {
                      const isSelected = selectedColorLine === line.meta_name;
                      const depth = line.level - 1;
                      const isAnchor = line.level === 1;
                      return (
                        <div
                          key={line.meta_name}
                          onClick={() => {
                            if (line.meta_name !== selectedColorLine) {
                              setSelectedColorLine(line.meta_name);
                              lastRefreshTreemapParams.current = {};
                            }
                            setRefAnchorEl(null);
                          }}
                          style={{
                            padding: "5px 12px 5px " + (12 + depth * 14) + "px",
                            cursor: "pointer",
                            fontSize: isAnchor ? 11.5 : 11,
                            fontWeight: isAnchor ? 600 : 400,
                            color: isSelected
                              ? "#fff"
                              : isAnchor
                                ? "#333"
                                : "#666",
                            backgroundColor: isSelected
                              ? clientColor
                              : "transparent",
                            borderLeft: isSelected
                              ? "3px solid " + clientColor
                              : "3px solid transparent",
                            transition: "background-color 0.12s",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected)
                              e.currentTarget.style.backgroundColor = "#f5f5f5";
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected)
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                          }}
                        >
                          <span>{line.name}</span>
                          {line.is_cost && (
                            <span
                              style={{
                                fontSize: 9,
                                color: isSelected ? "#fff" : "#c62828",
                                fontWeight: 500,
                                opacity: 0.7,
                              }}
                            >
                              (-)
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </span>
      {/* Global / Local scale selector */}
      <ActionDropdown
        button={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              padding: "3px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
              background: "#fff",
              fontSize: 11,
              fontWeight: 500,
              color: "#444",
            }}
          >
            <FontAwesomeIcon icon={faCrosshairs} style={{ fontSize: 12 }} />
            <span>
              {scaleMode === "global"
                ? t("treemap.scale_global")
                : t("treemap.scale_local")}
            </span>
          </div>
        }
        dropdownList={[
          {
            identified: { option_type: "scale_mode", value: "global" },
            text:
              t("treemap.scale_global") +
              " — " +
              t("treemap.scale_global_tooltip"),
            active: scaleMode === "global",
          },
          {
            identified: { option_type: "scale_mode", value: "local" },
            text:
              t("treemap.scale_local") +
              " — " +
              t("treemap.scale_local_tooltip"),
            active: scaleMode === "local",
          },
        ]}
        onClick={(opt) => {
          if (opt.option_type === "scale_mode" && opt.value !== scaleMode) {
            setScaleMode(opt.value);
          }
        }}
        dropPlacement="bottom-start"
      />
    </>
  );

  // Props compartilhados entre o treemap normal e o de foco
  const treemapProps = {
    isLoading: isLoading || isTreemapLoading,
    levels,
    dimensionNames,
    setSelection,
    setPnlSelection,
    levelsCount,
    parentLevel,
    setParentLevel,
    childrenLevel,
    data: treemapData,
    totalNodes,
    totalSize,
    colorMetric,
    scaleMode,
    globalStats,
    localStats,
    invertColors: isCostLine,
    selectedBox,
    setSelectedBox,
    showValues: true,
    valueNames: {
      size: plStructure.current.find(
        (obj) => obj.meta_name === netRevenueAnchor.current,
      )?.name,
      color:
        colorMetric === "serve_margin"
          ? eligibleColorLines.current.find(
              (l) => l.meta_name === selectedColorLine,
            )?.name ||
            plStructure.current.find(
              (obj) => obj.meta_name === serveMarginAnchor.current,
            )?.name
          : t("results.value") + " / " + volumeUnit.current,
      currency: currencyUnit,
    },
  };

  return (
    <>
      <div>
        <Hidden smDown>
          <ActionBar actions={graphOptions} spinnerIn={false} />
        </Hidden>
        <div ref={graphExplorerRef}>
          <GridContainer style={{ marginTop: "-28px" }}>
            <Hidden mdUp>
              <GridItem xs={12}>
                <ActionBar actions={graphOptions} spinnerIn={false} sm />
              </GridItem>
            </Hidden>
            <GridItem xs={12} style={{ position: "relative", padding: 0 }}>
              {!isLoading && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    right: 5,
                    display: "flex",
                    gap: 6,
                    zIndex: 1030,
                    marginBottom: -12,
                  }}
                >
                  {discreteControls}
                </div>
              )}
              <ResultsFilterBar
                filtersList={filtersList}
                filtersNames={filtersNames}
                filtersGroups={filtersGroups}
                commonFilters={commonFilters}
                commonFiltersMode={commonFiltersMode}
                commonFiltersSequence={commonFiltersSequence}
                filterMenuClickHandler={filterMenuClickHandler}
                removeFilter={removeFilter}
                dimFilterDialogProps={dimFilterDialogProps}
                clientColor={clientColor}
                sections={dimSections}
                showPeriodExtras={true}
                periodExtrasProps={{
                  availablePeriods,
                  selectedPeriodKeys,
                  activePreset,
                  onPeriodApply: handlePeriodApply,
                  persist,
                  onPersistChange: setPersist,
                  onReset: handleResetFilters,
                }}
                rangeFilters={rangeFilters}
                clearRangeFilter={clearRangeFilter}
              />
            </GridItem>
            <GridItem xs={12}>
              <Card style={{ marginTop: "0", marginBottom: "0" }}>
                <CardBody style={{ padding: "8px" }}>
                  <DraggableCanvas
                    isLoading={isLoading}
                    dimensions={dimensions}
                    dimensionNames={dimensionNames}
                    dimensionGroups={dimensionGroups}
                    groupsName={groupsName}
                    levels={levels}
                    setLevels={setLevels}
                    disabled={isTreemapLoading}
                  />
                </CardBody>
              </Card>
            </GridItem>
            <GridItem xs={12}>
              <Card style={{ marginTop: "8px", marginBottom: "0" }}>
                <CardBody style={{ padding: "6px" }}>{followMe}</CardBody>
              </Card>
            </GridItem>
            <GridItem xs={12} md={8}>
              <Card className={classes.dataCard}>
                <CardBody
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    padding: "6px 2px 2px 2px",
                  }}
                >
                  <Treemap {...treemapProps} />
                </CardBody>
              </Card>
            </GridItem>
            <GridItem xs={12} md={4}>
              <Card className={classes.dataCard}>
                <CardBody style={{ padding: "0" }}>
                  <PnlSharedProvider
                    plValues={plValues}
                    plStructure={plStructure}
                    benchmark={benchmark}
                    revenueReference={revenueReference}
                    netRevenueAnchor={netRevenueAnchor}
                    serveMarginAnchor={serveMarginAnchor}
                    oom={oom}
                    t={t}
                  >
                    <Pnl
                      plStructure={plStructure}
                      netRevenueAnchor={netRevenueAnchor}
                      serveMarginAnchor={serveMarginAnchor}
                      plName={plName}
                      isRefreshing={plRefreshing}
                      plValues={plValues}
                      volumeUnit={volumeUnit}
                      volumeL12M={volumeL12M}
                      currencyUnit={currencyUnit}
                      showValueType="value"
                      showColumns={["name", "val", "val_volume", "percentage"]}
                      isLoading={isLoading}
                      drillDownsOpen={drillDownsOpen}
                      setDrillDownsOpen={setDrillDownsOpen}
                      oom={oom}
                      filterScopeData={buildFilterScopeData()}
                    />
                  </PnlSharedProvider>
                </CardBody>
              </Card>
            </GridItem>
          </GridContainer>
        </div>
      </div>
      {/* Focus mode overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#eeeeee",
          zIndex: 1200,
          opacity: focusMode ? 1 : 0,
          visibility: focusMode ? "visible" : "hidden",
          transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
          padding: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 6,
            gap: 8,
          }}
        >
          {/* Discrete controls (unit, ref, scale) */}
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {discreteControls}
          </div>
          {/* Separator */}
          <div
            style={{
              width: 1,
              height: 20,
              backgroundColor: "#ddd",
              flexShrink: 0,
            }}
          />
          {/* Inline filters */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              flex: 1,
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexShrink: 0,
              }}
            >
              <FilterDropdown
                dropPlacement={"bottom-start"}
                hoverColor={"client"}
                dropdownHeader={t("results.add_change_filter")}
                dropdownList={filtersListTranslated()}
                dropdownActiveItems={filtersActiveList}
                dropdownFilterSequence={filtersSequenceNums()}
                tooltip={t("results.add_change_filter")}
                onClick={(ev) =>
                  filterMenuClickHandler()(
                    typeof ev === "string" ? ev : filtersList.current[ev],
                  )
                }
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                overflow: "auto",
                flexWrap: "nowrap",
              }}
            >
              {getFiltersInSequence()
                .filter((f) => f !== "period")
                .map((filter) => (
                  <FilterTag
                    key={filter}
                    filtersList={filtersList.current}
                    filtersNames={filtersNames.current}
                    filtersGroups={filtersGroups.current}
                    filter={filter}
                    items={getFilterItems(
                      commonFilters[filter] || [],
                      filter,
                      commonFiltersMode[filter],
                    )}
                    indexes={commonFiltersIndexes[filter] || []}
                    mode={commonFiltersMode[filter] || ""}
                    nameClickHandler={(ev) =>
                      filterMenuClickHandler()(
                        typeof ev === "string" ? ev : filtersList.current[ev],
                      )
                    }
                    removeItemHandler={(f, item, idx, all) =>
                      removeFilter(f, item, idx, all)
                    }
                    color={"gray"}
                  />
                ))}
              {Object.entries(rangeFilters).map(([dim, rf]) => (
                <FilterTag
                  key={`ref-range-fm-${dim}`}
                  filtersList={[`_ref_${dim}`]}
                  filtersNames={{
                    [`_ref_${dim}`]: {
                      [i18n.language]: dimNameForTag(dim),
                    },
                  }}
                  filter={`_ref_${dim}`}
                  items={[rf.label || ""]}
                  indexes={[0]}
                  mode="items"
                  removeItemHandler={() => clearRangeFilter(dim)}
                  color="gray"
                />
              ))}
            </div>
          </div>
          {/* Exit focus mode */}
          <button
            onClick={() => setFocusMode(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
              background: "#fff",
              fontSize: 12,
              color: "#666",
              flexShrink: 0,
            }}
          >
            <FullscreenExit style={{ fontSize: 16 }} />
            <span>{t("logistics_flow.focus_mode")}</span>
          </button>
        </div>
        <div style={{ flex: 1, display: "flex", gap: 8, overflow: "hidden" }}>
          {focusMode && (
            <>
              <div
                style={{ flex: 2, display: "flex", flexDirection: "column" }}
              >
                <Card style={{ margin: 0, flex: 1 }}>
                  <CardBody
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      padding: "6px 2px 2px 2px",
                    }}
                  >
                    <Treemap {...treemapProps} />
                  </CardBody>
                </Card>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 280,
                }}
              >
                <Card style={{ margin: 0, flex: 1, overflow: "auto" }}>
                  <CardBody style={{ padding: "0" }}>
                    <PnlSharedProvider
                      plValues={plValues}
                      plStructure={plStructure}
                      benchmark={benchmark}
                      revenueReference={revenueReference}
                      netRevenueAnchor={netRevenueAnchor}
                      serveMarginAnchor={serveMarginAnchor}
                      oom={oom}
                      t={t}
                    >
                      <Pnl
                        plStructure={plStructure}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        plName={plName}
                        isRefreshing={plRefreshing}
                        plValues={plValues}
                        volumeUnit={volumeUnit}
                        volumeL12M={volumeL12M}
                        currencyUnit={currencyUnit}
                        showValueType="value"
                        showColumns={[
                          "name",
                          "val",
                          "val_volume",
                          "percentage",
                        ]}
                        isLoading={isLoading}
                        drillDownsOpen={drillDownsOpen}
                        setDrillDownsOpen={setDrillDownsOpen}
                        oom={oom}
                        filterScopeData={buildFilterScopeData()}
                      />
                    </PnlSharedProvider>
                  </CardBody>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
      <FullPageLoader isVisible={isLoading} />
      {/* DimFilterDialog — servido pelo useResultsFilters (funciona tanto no
          modo normal via ResultsFilterBar quanto no focus mode via FilterDropdown) */}
      {focusMode && (
        <DimFilterDialog {...dimFilterDialogProps} clientColor={clientColor} />
      )}
      <HelpDialog
        open={helpDialogOpen}
        setOpen={setHelpDialogOpen}
        content={[
          {
            icon: "Purpose",
            title: t("help.graph_explorer.purpose.title"),
            items: [
              {
                title: t("help.graph_explorer.purpose.objective.title"),
                description: t(
                  "help.graph_explorer.purpose.objective.description",
                ),
              },
            ],
          },
          {
            icon: "BasicFunctionality",
            title: t("help.graph_explorer.basic_functionality.title"),
            items: [
              {
                title: t("help.graph_explorer.basic_functionality.step1.title"),
                description: t(
                  "help.graph_explorer.basic_functionality.step1.description",
                ),
              },
              {
                title: t("help.graph_explorer.basic_functionality.step2.title"),
                description: t(
                  "help.graph_explorer.basic_functionality.step2.description",
                ),
              },
              {
                title: t("help.graph_explorer.basic_functionality.step3.title"),
                description: t(
                  "help.graph_explorer.basic_functionality.step3.description",
                ),
              },
            ],
          },
          {
            icon: "OtherFeatures",
            title: t("help.graph_explorer.other_features.title"),
            items: [
              {
                title: t("help.graph_explorer.other_features.currency.title"),
                description: t(
                  "help.graph_explorer.other_features.currency.description",
                ),
              },
              {
                title: t("help.graph_explorer.other_features.screenshot.title"),
                description: t(
                  "help.graph_explorer.other_features.screenshot.description",
                ),
              },
            ],
          },
        ]}
      />
    </>
  );
}

GraphExplorer.propTypes = {};
